import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import Logo from '../components/Logo'
import CodeEditor from '../components/CodeEditor'
import MarkdownRenderer from '../components/MarkdownRenderer'
import HardwareTopology from '../components/HardwareTopology'
import { findProviderByBackend } from '../data/hardwareProviders'
import { getCalibrationData } from '../data/hardwareCalibrationData'
import { useAuth } from '../contexts/AuthContext'
import { useJobPolling } from '../hooks/useJobPolling'
import QueueStatusPanel from '../components/job/QueueStatusPanel'
import SubmissionHistoryList from '../components/job/SubmissionHistoryList'
import HardwareResultBar from '../components/job/HardwareResultBar'
import type { JobTrackingApi, SubmissionEntry } from '../types/jobTracking'
import { memoryApi, MemoryInfo, MemorySimResult, MemorySubmissionResult } from '../utils/api'

export default function QuantumMemoryPage() {
  const { memoryId } = useParams<{ memoryId: string }>()
  const { user } = useAuth()
  const [publicInfo, setPublicInfo] = useState<MemoryInfo | null>(null)

  // IBM API key (user's own key for unlimited submissions)
  const [ibmApiKey, setIbmApiKey] = useState('')

  // QStabIR code editor (ScaLER Python API)
  const [stabCode, setStabCode] = useState(`from scalerqec.QEC.qeccircuit import StabCode

# Define a [[5,1,3]] perfect code
qeccirc = StabCode(n=5, k=1, d=3)
qeccirc.add_stab("XZZXI")
qeccirc.add_stab("IXZZX")
qeccirc.add_stab("XIXZZ")
qeccirc.add_stab("ZXIXZ")
qeccirc.set_logical_Z(0, "ZZZZZ")

# Configure measurement scheme
qeccirc.scheme = "Standard"
qeccirc.rounds = 3

# Compile to Stim circuit
qeccirc.construct_circuit()
circuit = qeccirc.stimcirc
`)

  // Simulation
  const [noiseLevel, setNoiseLevel] = useState(0.001)
  const [noiseLevelStr, setNoiseLevelStr] = useState('0.001')
  const simShots = 10000
  const [simResult, setSimResult] = useState<MemorySimResult | null>(null)
  const [isSimulating, setIsSimulating] = useState(false)
  const [simError, setSimError] = useState('')

  // Hardware
  const [hwBackend, setHwBackend] = useState('')
  const [hwShots, setHwShots] = useState(10000)
  const qecRounds = 3
  const [layoutJson, setLayoutJson] = useState('{}')
  const [hwResult, setHwResult] = useState<MemorySubmissionResult | null>(null)
  const [isSubmittingHw, setIsSubmittingHw] = useState(false)
  const [hwError, setHwError] = useState('')
  const [showLayoutPreview, setShowLayoutPreview] = useState(false)
  const [showScurvePlot, setShowScurvePlot] = useState(false)
  const [dataLayoutStr, setDataLayoutStr] = useState('')
  const [ancLayoutStr, setAncLayoutStr] = useState('')

  // Compile preview: qubit classification (data vs ancilla) + IR + Stim
  const [compileInfo, setCompileInfo] = useState<{
    n_data?: number; n_ancilla?: number; total_qubits?: number;
    data_qubit_indices?: number[]; ancilla_qubit_indices?: number[];
    num_measurements?: number; num_detectors?: number; num_observables?: number;
  } | null>(null)
  const [irText, setIrText] = useState('')
  const [stimText, setStimText] = useState('')
  const [qiskitText, setQiskitText] = useState('')
  const [outputView, setOutputView] = useState<'stim' | 'qiskit' | 'transpiled' | 'noisy'>('qiskit')
  const [noisyStimText, setNoisyStimText] = useState('')
  const [isInjectingNoise, setIsInjectingNoise] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null)
  const [isCompiling, setIsCompiling] = useState(false)
  const [compileError, setCompileError] = useState('')

  // Transpile (Qiskit + layout → hardware-ready code)
  const [transpiledQasm, setTranspiledQasm] = useState('')
  const [transpileInfo, setTranspileInfo] = useState<{ qubit_count?: number; gate_count?: number; depth?: number } | null>(null)
  const [isTranspiling, setIsTranspiling] = useState(false)
  const [transpileError, setTranspileError] = useState('')

  const handleCompile = async () => {
    setIsCompiling(true)
    setCompileError('')
    try {
      const result = await memoryApi.compilePreview(stabCode)
      if (result.success) {
        setCompileInfo(result)
        setIrText(result.ir_text || '(No IR available)')
        setStimText(result.stim_circuit_str || '(No Stim circuit)')
        setQiskitText(result.qiskit_qasm || '(No Qiskit circuit)')
        setCompileError('')
      } else {
        setCompileError(result.error || 'Compilation failed')
        setCompileInfo(null)
        setIrText('')
        setStimText('')
        setQiskitText('')
      }
    } catch (e: any) {
      setCompileError(e.message || 'Compilation failed')
      setCompileInfo(null)
      setIrText('')
      setStimText('')
      setQiskitText('')
    } finally {
      setIsCompiling(false)
    }
  }

  // Transpile: apply layout to Qiskit circuit
  const handleTranspile = async () => {
    if (!qiskitText || !compileInfo) return
    const dataNums = dataLayoutStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
    const ancNums = ancLayoutStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
    if (dataNums.length !== (compileInfo.n_data || 0)) {
      setTranspileError(`Need exactly ${compileInfo.n_data} data qubits, got ${dataNums.length}`)
      return
    }
    if (ancNums.length !== (compileInfo.n_ancilla || 0)) {
      setTranspileError(`Need exactly ${compileInfo.n_ancilla} ancilla qubits, got ${ancNums.length}`)
      return
    }
    setIsTranspiling(true)
    setTranspileError('')
    try {
      const result = await memoryApi.transpileWithLayout({
        code: stabCode,
        data_layout: dataNums,
        anc_layout: ancNums,
        backend_name: hwBackend || undefined,
      })
      if (result.success) {
        setTranspiledQasm(result.transpiled_qasm || '')
        setTranspileInfo({ qubit_count: result.qubit_count, gate_count: result.gate_count, depth: result.depth })
        setTranspileError('')
        setOutputView('transpiled')  // Auto-switch to show transpiled code
      } else {
        setTranspileError(result.error || 'Transpilation failed')
      }
    } catch (e: any) {
      setTranspileError(e.message || 'Transpilation failed')
    } finally {
      setIsTranspiling(false)
    }
  }

  // Inject noise model into Stim circuit
  const handleInjectNoise = async () => {
    if (!stimText) return
    setIsInjectingNoise(true)
    try {
      const result = await memoryApi.injectNoise(stimText, noiseLevel)
      if (result.success && result.noisy_circuit_str) {
        setNoisyStimText(result.noisy_circuit_str)
        setOutputView('noisy')
      } else {
        alert(result.error || 'Failed to inject noise')
      }
    } catch (e: any) {
      alert(e.message || 'Failed to inject noise')
    } finally {
      setIsInjectingNoise(false)
    }
  }

  // Sync data + ancilla layout strings into unified layoutJson
  useEffect(() => {
    if (!compileInfo) return
    const nData = compileInfo.n_data || 0
    const dataNums = dataLayoutStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
    const ancNums = ancLayoutStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
    const combined: Record<string, number> = {}
    dataNums.forEach((pq, i) => { if (i < nData) combined[String(i)] = pq })
    ancNums.forEach((pq, i) => { combined[String(nData + i)] = pq })
    if (Object.keys(combined).length > 0) {
      setLayoutJson(JSON.stringify(combined))
    }
  }, [dataLayoutStr, ancLayoutStr, compileInfo])

  // Parse layout into qubit arrays for topology visualization
  // Supports: {"0": 17, "1": 18} (dict) or [17, 18, 23] (array) or "17, 18, 23" (comma-separated)
  const { layoutQubits, layoutLabels, dataPhysQubits, ancillaPhysQubits } = useMemo(() => {
    const qubits: number[] = []
    const labels: Record<number, string> = {}
    const dataPhys: number[] = []
    const ancillaPhys: number[] = []
    const trimmed = layoutJson.trim()
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return { layoutQubits: qubits, layoutLabels: labels, dataPhysQubits: dataPhys, ancillaPhysQubits: ancillaPhys }

    // Parse qubit list
    const parsedQubits: { stimIdx: number; physIdx: number }[] = []
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        parsed.forEach((physQ: number, i: number) => {
          const pq = Number(physQ)
          if (!isNaN(pq)) parsedQubits.push({ stimIdx: i, physIdx: pq })
        })
      } else if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([stimQ, physQ]) => {
          const pq = Number(physQ)
          if (!isNaN(pq)) parsedQubits.push({ stimIdx: Number(stimQ), physIdx: pq })
        })
      }
    } catch {
      const nums = trimmed.replace(/[\[\]{}]/g, '').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
      nums.forEach((pq, i) => parsedQubits.push({ stimIdx: i, physIdx: pq }))
    }

    const nData = compileInfo?.n_data ?? 0
    parsedQubits.forEach(({ stimIdx, physIdx }) => {
      qubits.push(physIdx)
      const isData = nData > 0 && stimIdx < nData
      const prefix = nData > 0 ? (isData ? 'D' : 'A') : 'q'
      const localIdx = isData ? stimIdx : stimIdx - nData
      labels[physIdx] = `${prefix}${localIdx}`
      if (nData > 0) {
        if (isData) dataPhys.push(physIdx)
        else ancillaPhys.push(physIdx)
      }
    })

    return { layoutQubits: qubits, layoutLabels: labels, dataPhysQubits: dataPhys, ancillaPhysQubits: ancillaPhys }
  }, [layoutJson, compileInfo])

  // Sidebar resize
  const [sidebarWidth, setSidebarWidth] = useState(220)
  const isResizing = useRef(false)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    const startX = e.clientX
    const startW = sidebarWidth
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return
      const newW = Math.max(160, Math.min(500, startW + ev.clientX - startX))
      setSidebarWidth(newW)
    }
    const onUp = () => { isResizing.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [sidebarWidth])

  // Column resize (3-pane editor)
  const [col1Pct, setCol1Pct] = useState(33.3)
  const [col2Pct, setCol2Pct] = useState(33.3)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const makeColResizeHandler = useCallback((setLeft: (v: number) => void, setRight: (v: number) => void, getLeftPct: () => number, getRightPct: () => number) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      const container = editorContainerRef.current
      if (!container) return
      const containerW = container.getBoundingClientRect().width
      const startX = e.clientX
      const startLeft = getLeftPct()
      const startRight = getRightPct()
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX
        const dPct = (dx / containerW) * 100
        const newLeft = Math.max(15, Math.min(70, startLeft + dPct))
        const newRight = Math.max(15, startRight - dPct)
        setLeft(newLeft)
        setRight(newRight)
      }
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }
  }, [])
  const handleCol1Resize = useCallback((e: React.MouseEvent) => {
    makeColResizeHandler(setCol1Pct, setCol2Pct, () => col1Pct, () => col2Pct)(e)
  }, [col1Pct, col2Pct, makeColResizeHandler])
  const handleCol2Resize = useCallback((e: React.MouseEvent) => {
    const col3Pct = 100 - col1Pct - col2Pct
    makeColResizeHandler(setCol2Pct, (_v) => {/* col3 is auto */}, () => col2Pct, () => col3Pct)(e)
  }, [col1Pct, col2Pct, makeColResizeHandler])

  // Job tracking API adapter
  const jobApi = useMemo<JobTrackingApi>(() => ({
    getQueueStatus: async (entityId: string) => {
      const q = await memoryApi.getQueueStatus(entityId)
      return {
        totalQueued: q.total_queued,
        totalRunning: q.total_running,
        estimatedWaitMinutes: q.estimated_wait_minutes,
        queue: q.queue.map(j => ({ id: j.id, label: j.student_label, position: j.position, backend: j.backend, submittedAt: j.submitted_at })),
        running: q.running.map(j => ({ id: j.id, label: j.student_label, backend: j.backend, startedAt: j.started_at })),
        myJobs: q.my_submissions.map(j => ({ id: j.id, position: j.position, status: j.status })),
      }
    },
    getSubmissions: async () => {
      if (!memoryId) return []
      try {
        const r = await memoryApi.adminGetSubmissions(memoryId, 1, 20)
        return r.submissions.map((s: any) => ({
          id: s.id,
          status: s.status,
          backend: s.backend_name,
          createdAt: s.created_at,
          errorMessage: s.error_message,
          logical_error_rate: s.logical_error_rate,
          num_logical_errors: s.num_logical_errors,
          num_shots_decoded: s.num_shots_decoded,
          qec_rounds: s.qec_rounds,
          shots: s.shots,
          ibmq_job_id: s.ibmq_job_id,
          participant: s.participant?.display_name,
        }))
      } catch { return [] }
    },
    getSubmissionStatus: async (submissionId: string) => {
      try {
        const s = await memoryApi.getStatus(submissionId, '')
        return {
          id: s.id, status: s.status, backend: s.backend_name,
          createdAt: s.created_at, errorMessage: s.error_message,
          logical_error_rate: s.logical_error_rate,
        } as SubmissionEntry & { logical_error_rate?: number }
      } catch { return null }
    },
  }), [])

  const {
    queueStatus, submissions, isLoadingQueue,
    trackSubmission, trackedSubmission,
  } = useJobPolling({ entityId: memoryId, api: jobApi, enabled: true, pollIntervalMs: 15000 })

  // Draft save
  const draftKey = `qm_draft_${memoryId}`

  // Helper to build the circuit definition for API calls
  const getCircuitParams = () => {
    return { code: stabCode }
  }

  // Load public info
  useEffect(() => {
    if (!memoryId) return
    memoryApi.getInfo(memoryId).then(info => {
      setPublicInfo(info)
      if (info.allowed_backends?.length > 0 && !hwBackend) {
        setHwBackend(info.allowed_backends[0])
      }
      if (info.noise_levels?.length > 0) {
        setNoiseLevel(info.noise_levels[0])
      }
    }).catch(() => {})

    // Restore draft
    const draft = localStorage.getItem(draftKey)
    if (draft) {
      setStabCode(draft)
    }

  }, [memoryId])


  // Load starter code
  useEffect(() => {
    if (publicInfo?.starter_code && !localStorage.getItem(draftKey)) {
      setStabCode(publicInfo.starter_code)
    }
  }, [publicInfo])

  // Simulate (dual: Stim MC + ScaLER)
  // If already compiled, send the Stim circuit directly (fast path, skips re-execution)
  const handleSimulate = async () => {
    if (!memoryId) return
    setIsSimulating(true)
    setSimError('')
    setSimResult(null)
    try {
      const params: any = {
        memory_id: memoryId,
        noise_level: noiseLevel,
        shots: simShots,
      }
      if (stimText && compileInfo) {
        // Fast path: send pre-compiled Stim circuit
        params.stim_circuit_str = stimText
        params.code_distance = compileInfo.n_data ? Math.round(Math.sqrt(compileInfo.n_data)) : 3
      } else {
        // Slow path: send Python code for re-execution
        Object.assign(params, getCircuitParams())
      }
      const result = await memoryApi.simulate(params)
      if (result.success) {
        setSimResult(result)
      } else {
        setSimError(result.error || 'Simulation failed')
      }
    } catch (e: any) {
      setSimError(e.message || 'Simulation failed')
    } finally {
      setIsSimulating(false)
    }
  }

  // Hardware submit
  const handleSubmitHardware = async () => {
    if (!memoryId) return
    if (!transpiledQasm) {
      setHwError('Please click "Transpile" first to generate the hardware-ready circuit before submitting.')
      return
    }
    setIsSubmittingHw(true)
    setHwError('')
    setHwResult(null)
    try {
      let layout: Record<string, number>
      try {
        layout = JSON.parse(layoutJson)
      } catch {
        setHwError('Invalid layout JSON')
        setIsSubmittingHw(false)
        return
      }

      const result = await memoryApi.submitHardware({
        memory_id: memoryId,
        backend: hwBackend,
        layout,
        qec_rounds: qecRounds,
        shots: hwShots,
        ...(stimText ? { stim_circuit_str: stimText } : getCircuitParams()),
        ...(ibmApiKey ? { ibmq_api_key: ibmApiKey } : {}),
      })
      setHwResult(result)
      // Start tracking this submission
      if (result.id) trackSubmission(result.id)
    } catch (e: any) {
      setHwError(e.message || 'Submission failed')
    } finally {
      setIsSubmittingHw(false)
    }
  }

  // Build per-qubit highlight colors: data=blue, ancilla=orange
  const highlightColors = useMemo(() => {
    const colors: Record<number, string> = {}
    dataPhysQubits.forEach(pq => { colors[pq] = '#3b82f6' })      // blue for data
    ancillaPhysQubits.forEach(pq => { colors[pq] = '#f97316' })   // orange for ancilla
    return colors
  }, [dataPhysQubits, ancillaPhysQubits])

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header — matches HomeworkPage */}
      <header className="h-12 flex-shrink-0 bg-white border-b border-qcloud-border px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="small" />
            <span className="text-lg font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">QuantumArena</span>
          </Link>
          <span className="text-qcloud-muted">/</span>
          <span className="text-sm font-semibold text-qcloud-text">{publicInfo?.title || 'QEC Challenge'}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {memoryId && (
            <>
              <Link to={`/memory/${memoryId}/leaderboard`} className="px-3 py-1.5 text-green-600 font-medium hover:text-green-700">Leaderboard</Link>
              <Link to={`/memory/${memoryId}/queue`} className="px-3 py-1.5 text-qcloud-muted hover:text-qcloud-primary">Queue</Link>
              <Link to={`/memory/${memoryId}/leaderboard?view=hardware`} className="px-3 py-1.5 text-qcloud-muted hover:text-qcloud-primary">Hardware Ranking</Link>
              {user?.is_admin && (
                <Link to="/admin/qec" className="px-3 py-1.5 text-red-400 font-medium hover:text-red-500">Admin</Link>
              )}
            </>
          )}
        </div>
      </header>

      {/* Top toolbar — dark bar with controls, matches HomeworkPage */}
      <div className="h-9 flex-shrink-0 bg-slate-800 px-4 flex items-center justify-between">
        {/* Left: editor tabs */}
        <div className="flex items-center gap-1">
          <span className="px-3 py-1.5 rounded-t text-xs flex items-center gap-1.5 bg-slate-900 text-slate-200 border-b-2 border-blue-400">
            &lt;/&gt; QStabIR Editor
          </span>
          <Link to="/stabilizer-editor" className="px-3 py-1.5 rounded-t text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1.5">
            Visual Editor
          </Link>
          <a href="https://arxiv.org/abs/2602.04921" target="_blank" rel="noopener noreferrer"
            className="px-2 py-1 rounded text-[10px] text-slate-500 hover:text-blue-300 hover:bg-slate-700">
            ScaLER Paper
          </a>
          <div className="w-px h-4 bg-slate-600 mx-1" />
          <span className="text-[10px] text-slate-500">Draft saved</span>
          <button onClick={() => { setStabCode(publicInfo?.starter_code || stabCode); localStorage.removeItem(draftKey) }}
            className="text-[10px] text-red-400 hover:text-red-300 ml-1">Reset</button>
        </div>
        {/* Right: backend + actions */}
        <div className="flex items-center gap-1.5">
          {/* Backend selector */}
          <span className="text-xs text-slate-400">Backend:</span>
          <select value={hwBackend} onChange={e => setHwBackend(e.target.value)}
            className="px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none">
            {(publicInfo?.allowed_backends || []).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <div className="w-px h-4 bg-slate-600" />
          {/* Shots */}
          <span className="text-xs text-slate-400">Shots:</span>
          <input type="number" value={hwShots} onChange={e => setHwShots(Number(e.target.value))}
            min={1000} max={100000} step={1000}
            className="w-16 px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none" />
          <div className="w-px h-4 bg-slate-600" />
          {/* Layout button */}
          {hwBackend && getCalibrationData(hwBackend) && (
            <button onClick={() => setShowLayoutPreview(true)}
              className="px-2 py-1 bg-slate-700 text-blue-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-blue-100"
              title="Preview hardware topology and qubit layout">Layout</button>
          )}
          {/* Compile */}
          <button onClick={handleCompile} disabled={isCompiling}
            className="px-2 py-1 bg-slate-700 text-purple-300 border border-slate-600 rounded text-xs hover:bg-slate-600 disabled:opacity-50">
            {isCompiling ? '...' : 'Compile'}
          </button>
          {compileInfo && <span className="text-[10px] text-green-400">{compileInfo.n_data}D+{compileInfo.n_ancilla}A={compileInfo.total_qubits}q</span>}
          {compileError && <span className="text-[10px] text-red-400" title={compileError}>Err</span>}
          {/* Transpile (requires compile + layout) */}
          <button onClick={handleTranspile}
            disabled={isTranspiling || !compileInfo || !dataLayoutStr.trim() || !ancLayoutStr.trim()}
            className="px-2 py-1 bg-slate-700 text-yellow-300 border border-slate-600 rounded text-xs hover:bg-slate-600 disabled:opacity-50"
            title={!compileInfo ? 'Compile first' : !dataLayoutStr.trim() ? 'Set data layout' : !ancLayoutStr.trim() ? 'Set ancilla layout' : 'Apply layout to generate hardware-ready circuit'}>
            {isTranspiling ? '...' : 'Transpile'}
          </button>
          {transpileInfo && <span className="text-[10px] text-yellow-400">{transpileInfo.gate_count}g d={transpileInfo.depth}</span>}
          {transpileError && <span className="text-[10px] text-red-400" title={transpileError}>Err</span>}
          {/* Noise for sim */}
          <span className="text-xs text-slate-400">p:</span>
          <input type="text" value={noiseLevelStr}
            onChange={e => { setNoiseLevelStr(e.target.value); const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0 && v <= 1) setNoiseLevel(v) }}
            className="w-16 px-1 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="Physical error rate (e.g. 0.001)" />
          {/* Inject Noise */}
          <button onClick={handleInjectNoise} disabled={isInjectingNoise || !stimText}
            className="px-2 py-1 bg-slate-700 text-orange-300 border border-slate-600 rounded text-xs hover:bg-slate-600 disabled:opacity-50"
            title="Inject SID noise model into the Stim circuit">
            {isInjectingNoise ? '...' : 'Inject Noise'}
          </button>
          <div className="w-px h-4 bg-slate-600" />
          {/* Test LER by Simulation */}
          <button onClick={handleSimulate} disabled={isSimulating}
            className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-500 disabled:opacity-50">
            {isSimulating ? 'Testing LER...' : 'Test LER by Simulation'}
          </button>
          {/* Submit to Hardware */}
          <button onClick={handleSubmitHardware} disabled={isSubmittingHw || (!user && !ibmApiKey) || !transpiledQasm}
            className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-500 disabled:opacity-50"
            title={!user && !ibmApiKey ? 'Sign in or provide your IBM API key' : !transpiledQasm ? 'Click Transpile first to generate the hardware-ready circuit' : 'Submit to IBM hardware (3 free, or unlimited with your own key)'}>
            {isSubmittingHw ? 'Submitting...' : 'Submit to Hardware'}
          </button>
        </div>
      </div>

      {/* Simulation result panel */}
      {(simResult || simError) && (
        <div className="flex-shrink-0 border-b border-qcloud-border bg-slate-50">
          {simError && <div className="px-4 py-2 text-xs text-red-500">{simError}</div>}
          {simResult && (
            <div className="px-4 py-2 flex items-center gap-4 text-xs">
              <span className="font-semibold text-qcloud-text">Stim MC:</span>
              <span className="font-mono text-green-600 font-bold">
                LER = {simResult.logical_error_rate !== undefined ? simResult.logical_error_rate.toExponential(3) : 'N/A'}
              </span>
              <span className="text-qcloud-muted">({simResult.num_logical_errors}/{simResult.num_shots_decoded}, {simResult.execution_time_ms?.toFixed(0)}ms)</span>
              <div className="w-px h-4 bg-qcloud-border" />
              <span className="font-semibold text-qcloud-text">ScaLER:</span>
              <span className="font-mono text-purple-600 font-bold">
                LER = {simResult.scaler_logical_error_rate != null ? simResult.scaler_logical_error_rate.toExponential(3) : 'N/A'}
              </span>
              <span className="text-qcloud-muted">
                ({simResult.scaler_method === 'scaler_symbolic' ? 'Exact' : 'Stratified'}
                {simResult.scaler_r_squared != null && `, R\u00B2=${simResult.scaler_r_squared.toFixed(3)}`}
                {simResult.scaler_sweet_spot != null && `, w*=${simResult.scaler_sweet_spot}`}
                , {simResult.scaler_execution_time_ms?.toFixed(0)}ms)
              </span>
              {simResult.scaler_plots && (simResult.scaler_plots.scurve_png || simResult.scaler_plots.scurve_fit) && (
                <button onClick={() => setShowScurvePlot(true)}
                  className="px-2 py-1 bg-purple-600 text-white rounded text-[10px] font-semibold hover:bg-purple-500">
                  See Fitted S-curve
                </button>
              )}
              <div className="flex-1" />
              <button onClick={() => { setSimResult(null); setSimError('') }}
                className="text-qcloud-muted hover:text-red-400 text-[10px]">dismiss</button>
            </div>
          )}
        </div>
      )}

      {/* Hardware result bar */}
      {(hwResult || hwError || trackedSubmission) && (() => {
        const show = trackedSubmission || hwResult
        return show ? (
          <HardwareResultBar
            status={show.status}
            queuePosition={(show as any).queue_position}
            errorMessage={(show as any).error_message || (show as any).errorMessage}
            onDismiss={() => { setHwResult(null); setHwError('') }}
          >
            {(show as any).logical_error_rate != null && (
              <span className="font-mono text-blue-600 font-bold">LER = {(show as any).logical_error_rate.toExponential(3)}</span>
            )}
          </HardwareResultBar>
        ) : hwError ? (
          <div className="flex-shrink-0 border-b border-qcloud-border bg-red-50 px-4 py-2 text-xs text-red-500">
            {hwError}
            <button onClick={() => setHwError('')} className="ml-4 text-qcloud-muted hover:text-red-400">dismiss</button>
          </div>
        ) : null
      })()}

      {/* Main content: left sidebar + editor */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar — resizable */}
        <div className="flex-shrink-0 bg-white border-r border-qcloud-border overflow-y-auto" style={{ width: sidebarWidth }}>
          {/* Account — same pattern as other challenges */}
          <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Account</h4>
            {user ? (
              <div className="text-[10px] text-green-600 flex items-center gap-1">
                <span>Logged in as <strong>{user.username}</strong></span>
              </div>
            ) : (
              <>
                <p className="text-[10px] text-qcloud-muted mb-2">
                  Log in to submit circuits and appear on the leaderboard.
                </p>
                <div className="flex gap-2">
                  <a href="/login" className="flex-1 px-2 py-1.5 bg-qcloud-primary text-white rounded text-xs text-center hover:bg-qcloud-secondary">Sign In</a>
                  <a href="/register" className="flex-1 px-2 py-1.5 border border-qcloud-border rounded text-xs text-center hover:bg-gray-50">Sign Up</a>
                </div>
              </>
            )}
          </div>

          {/* YOUR IBM API KEY */}
          <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Your IBM API Key</h4>
            <input type="password" value={ibmApiKey} onChange={e => setIbmApiKey(e.target.value)}
              placeholder="Paste your IBM API key..."
              className="w-full px-2 py-1.5 border border-qcloud-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50" />
            <p className="text-[9px] text-qcloud-muted mt-1">Unlimited submissions with your own key. Stored in browser only.</p>
          </div>


          {/* Layout summary (details in modal) */}
          <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Layout</h4>
            {compileInfo ? (
              <div className="space-y-1 text-[10px]">
                <div className="flex items-center gap-1 text-blue-600">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  Data[{compileInfo.n_data}]: <span className="font-mono">{dataLayoutStr || 'not set'}</span>
                </div>
                <div className="flex items-center gap-1 text-orange-600">
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                  Anc[{compileInfo.n_ancilla}]: <span className="font-mono">{ancLayoutStr || 'not set'}</span>
                </div>
              </div>
            ) : (
              <p className="text-[10px] text-qcloud-muted">Compile first to set layout</p>
            )}
            {hwBackend && getCalibrationData(hwBackend) && (
              <button onClick={() => setShowLayoutPreview(true)}
                className="w-full mt-2 px-2 py-1.5 bg-slate-100 text-slate-700 text-xs rounded border border-slate-300 hover:bg-slate-200 font-medium">
                Open Layout Editor
              </button>
            )}
          </div>

          {/* Live Queue Status */}
          <QueueStatusPanel queueStatus={queueStatus} isLoading={isLoadingQueue} />

          {/* Submission History */}
          <SubmissionHistoryList
            submissions={submissions}
            onClickSubmission={(sub) => setSelectedSubmission(selectedSubmission?.id === sub.id ? null : sub)}
            renderMetric={(sub: any) =>
              sub.logical_error_rate != null ? (
                <span className="font-mono text-green-600">{sub.logical_error_rate.toExponential(2)}</span>
              ) : null
            }
          />

          {/* Submission Detail */}
          {selectedSubmission && (
            <div className="p-3 border-b border-qcloud-border bg-blue-50/50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-qcloud-text uppercase tracking-wider">Job Detail</h4>
                <button onClick={() => setSelectedSubmission(null)} className="text-qcloud-muted hover:text-red-400 text-[10px]">close</button>
              </div>
              <div className="space-y-1 text-[10px]">
                <div className="flex justify-between"><span className="text-qcloud-muted">ID:</span><span className="font-mono">{selectedSubmission.id}</span></div>
                <div className="flex justify-between"><span className="text-qcloud-muted">Status:</span>
                  <span className={`font-semibold ${selectedSubmission.status === 'completed' ? 'text-green-600' : selectedSubmission.status === 'failed' ? 'text-red-500' : 'text-blue-600'}`}>
                    {selectedSubmission.status}
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-qcloud-muted">Backend:</span><span>{selectedSubmission.backend}</span></div>
                {selectedSubmission.logical_error_rate != null && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">Logical Error Rate:</span>
                    <span className="font-mono font-bold text-blue-600">{(selectedSubmission.logical_error_rate * 100).toFixed(3)}%</span>
                  </div>
                )}
                {selectedSubmission.num_logical_errors != null && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">Logical Errors:</span>
                    <span className="font-mono">{selectedSubmission.num_logical_errors} / {selectedSubmission.num_shots_decoded}</span>
                  </div>
                )}
                {selectedSubmission.shots != null && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">Shots:</span><span className="font-mono">{selectedSubmission.shots?.toLocaleString()}</span></div>
                )}
                {selectedSubmission.qec_rounds != null && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">QEC Rounds:</span><span>{selectedSubmission.qec_rounds}</span></div>
                )}
                {selectedSubmission.ibmq_job_id && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">IBM Job:</span><span className="font-mono">{selectedSubmission.ibmq_job_id}</span></div>
                )}
                {selectedSubmission.createdAt && (
                  <div className="flex justify-between"><span className="text-qcloud-muted">Submitted:</span><span>{new Date(selectedSubmission.createdAt).toLocaleString()}</span></div>
                )}
                {selectedSubmission.errorMessage && (
                  <div className="text-red-500 mt-1">{selectedSubmission.errorMessage}</div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {publicInfo?.description && (
            <div className="p-3">
              <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Description</h4>
              <div className="max-h-60 overflow-y-auto">
                <MarkdownRenderer content={publicInfo.description} />
              </div>
            </div>
          )}
        </div>

        {/* Resize handle */}
        <div onMouseDown={handleResizeStart}
          className="w-2 flex-shrink-0 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors relative group"
          title="Drag to resize">
          <div className="absolute inset-y-0 -left-1 -right-1" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-400 group-hover:bg-blue-500 transition-colors" />
        </div>

        {/* 3-Column Pipeline Editor */}
        <div ref={editorContainerRef} className="flex-1 flex overflow-hidden">
          {/* Column 1: QStabIR Source */}
          <div className="flex flex-col min-w-0" style={{ width: `${col1Pct}%` }}>
            <div className="flex-shrink-0 px-3 py-1.5 bg-slate-100 border-b border-slate-300 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">1. QStabIR Code</span>
              <button onClick={handleCompile} disabled={isCompiling}
                className="px-3 py-1 bg-blue-600 text-white text-[10px] font-semibold rounded hover:bg-blue-500 disabled:opacity-50">
                {isCompiling ? 'Compiling...' : 'Compile \u2192'}
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <CodeEditor value={stabCode} onChange={(v) => { if (v !== undefined) { setStabCode(v); localStorage.setItem(draftKey, v) } }} />
            </div>
          </div>

          {/* Resize handle 1-2 */}
          <div onMouseDown={handleCol1Resize}
            className="w-1.5 flex-shrink-0 bg-slate-200 hover:bg-blue-400 cursor-col-resize transition-colors relative group">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-6 rounded-full bg-slate-400 group-hover:bg-blue-500" />
          </div>

          {/* Column 2: Compiled IR */}
          <div className="flex flex-col min-w-0" style={{ width: `${col2Pct}%` }}>
            <div className="flex-shrink-0 px-3 py-1.5 bg-slate-100 border-b border-slate-300 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">2. Compiled IR</span>
              {compileInfo && (
                <span className="text-[10px] text-green-600 font-mono">{compileInfo.n_data}D + {compileInfo.n_ancilla}A = {compileInfo.total_qubits}q</span>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-slate-50">
              {irText ? (
                <pre className="p-3 text-xs font-mono text-slate-700 whitespace-pre-wrap leading-relaxed">{irText}</pre>
              ) : compileError ? (
                <div className="p-3 text-xs text-red-500">{compileError}</div>
              ) : (
                <div className="p-3 text-xs text-slate-400 italic">Click "Compile" to generate the IR from your QStabIR code.</div>
              )}
            </div>
          </div>

          {/* Resize handle 2-3 */}
          <div onMouseDown={handleCol2Resize}
            className="w-1.5 flex-shrink-0 bg-slate-200 hover:bg-blue-400 cursor-col-resize transition-colors relative group">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-6 rounded-full bg-slate-400 group-hover:bg-blue-500" />
          </div>

          {/* Column 3: Output Circuit (Qiskit / Transpiled / Stim toggle) */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-shrink-0 px-3 py-1.5 bg-slate-100 border-b border-slate-300 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-xs font-semibold text-slate-700 mr-2">3.</span>
                <button onClick={() => setOutputView('qiskit')}
                  className={`px-2 py-0.5 text-[10px] rounded ${outputView === 'qiskit' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
                  Qiskit
                </button>
                <button onClick={() => setOutputView('transpiled')}
                  className={`px-2 py-0.5 text-[10px] rounded ${outputView === 'transpiled' ? 'bg-yellow-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
                  Transpiled
                </button>
                <button onClick={() => setOutputView('stim')}
                  className={`px-2 py-0.5 text-[10px] rounded ${outputView === 'stim' ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
                  Stim
                </button>
                <button onClick={() => setOutputView('noisy')}
                  className={`px-2 py-0.5 text-[10px] rounded ${outputView === 'noisy' ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
                  Noisy Stim
                </button>
              </div>
              <span className="text-[10px] text-slate-500">
                {outputView === 'qiskit' && compileInfo && `data[${compileInfo.n_data}] anc[${compileInfo.n_ancilla}]`}
                {outputView === 'transpiled' && transpileInfo && `${transpileInfo.qubit_count}q ${transpileInfo.gate_count}g d=${transpileInfo.depth}`}
                {outputView === 'noisy' && noisyStimText && `SID p=${noiseLevel}`}
                {outputView === 'stim' && compileInfo && `${compileInfo.num_measurements}m ${compileInfo.num_detectors}det`}
              </span>
            </div>
            <div className="flex-1 min-h-0">
              {outputView === 'qiskit' ? (
                qiskitText ? (
                  <CodeEditor value={qiskitText} onChange={(v) => { if (v !== undefined) setQiskitText(v) }} />
                ) : (
                  <div className="p-3 text-xs text-slate-400 italic h-full bg-slate-50">Click <b>Compile</b> to generate Qiskit circuit with data[] and anc[] registers.</div>
                )
              ) : outputView === 'transpiled' ? (
                transpiledQasm ? (
                  <CodeEditor value={transpiledQasm} onChange={(v) => { if (v !== undefined) setTranspiledQasm(v) }} />
                ) : (
                  <div className="p-3 text-xs text-slate-400 italic h-full bg-slate-50">
                    Click <b>Transpile</b> to apply layout mapping and generate hardware-ready circuit.
                    <br/><br/>
                    Requirements:<br/>
                    1. Compile your QStabIR code first<br/>
                    2. Set data and ancilla qubit layouts (click Layout button)<br/>
                    3. Click Transpile in the toolbar
                  </div>
                )
              ) : outputView === 'noisy' ? (
                noisyStimText ? (
                  <CodeEditor value={noisyStimText} onChange={(v) => { if (v !== undefined) setNoisyStimText(v) }} />
                ) : (
                  <div className="p-3 text-xs text-slate-400 italic h-full bg-slate-50">
                    Click <b>Inject Noise</b> in the toolbar to apply SID noise model (DEPOLARIZE1 before every gate) to the Stim circuit.
                  </div>
                )
              ) : (
                stimText ? (
                  <CodeEditor value={stimText} onChange={(v) => { if (v !== undefined) setStimText(v) }} />
                ) : (
                  <div className="p-3 text-xs text-slate-400 italic h-full bg-slate-50">Click <b>Compile</b> to generate Stim circuit.</div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <footer className="h-6 flex-shrink-0 bg-qcloud-primary px-4 text-[10px] flex items-center justify-between text-white/80">
        <span>Python - ScaLER QStabIR</span>
        <span className="flex items-center gap-4">
          <span>QStabIR Editor</span>
          <span>{hwBackend || 'No backend'}</span>
        </span>
      </footer>

      {/* ScaLER S-Curve Plot Modal */}
      {showScurvePlot && simResult?.scaler_plots && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowScurvePlot(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-qcloud-text">ScaLER Fitted S-Curve</h3>
                <p className="text-xs text-qcloud-muted mt-0.5">
                  LER = {simResult.scaler_logical_error_rate?.toExponential(3)}
                  {simResult.scaler_r_squared != null && ` | R\u00B2 = ${simResult.scaler_r_squared.toFixed(4)}`}
                  {simResult.scaler_sweet_spot != null && ` | Sweet spot w* = ${simResult.scaler_sweet_spot}`}
                </p>
              </div>
              <button onClick={() => setShowScurvePlot(false)} className="text-qcloud-muted hover:text-red-500 text-xl font-bold">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 flex justify-center items-start">
              {simResult.scaler_plots.scurve_png ? (
                <img src={`data:image/png;base64,${simResult.scaler_plots.scurve_png}`}
                  alt="ScaLER S-curve fit" className="max-w-full max-h-[70vh] rounded border border-slate-200 shadow-sm" />
              ) : simResult.scaler_plots.scurve_fit ? (
                <iframe src={`data:application/pdf;base64,${simResult.scaler_plots.scurve_fit}`}
                  width="100%" height="600" className="rounded border border-slate-200" title="ScaLER S-curve fit" />
              ) : (
                <p className="text-qcloud-muted text-sm">No plot available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hardware Layout Preview Modal */}
      {showLayoutPreview && hwBackend && (() => {
        const provider = findProviderByBackend(hwBackend)
        const calibData = getCalibrationData(hwBackend)
        if (!calibData) return null
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLayoutPreview(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="font-bold text-qcloud-text">Hardware Layout — {provider?.name || hwBackend}</h3>
                  <p className="text-xs text-qcloud-muted mt-0.5">
                    {provider && <span>{provider.qubits} qubits &bull; </span>}
                    Calibration: {calibData.calibrationDate.replace('T', ' ').replace('Z', ' UTC')}
                  </p>
                </div>
                <button onClick={() => setShowLayoutPreview(false)} className="text-qcloud-muted hover:text-red-500 text-lg">&times;</button>
              </div>
              <div className="overflow-y-auto flex-1 p-6 space-y-4">
                {/* Separate Data / Ancilla layout inputs */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  {compileInfo ? (
                    <>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-semibold text-qcloud-text">Qubit Layout</span>
                        <span className="text-qcloud-muted">Total: {compileInfo.total_qubits} qubits needed</span>
                      </div>
                      {/* Data qubits */}
                      <div>
                        <label className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-1">
                          <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
                          Data Qubits — need {compileInfo.n_data} physical qubits
                        </label>
                        <input type="text" value={dataLayoutStr} onChange={e => setDataLayoutStr(e.target.value)}
                          placeholder={`Enter ${compileInfo.n_data} physical qubit IDs, e.g. 19, 20, 21, 22, 23, 24, 25, 26, 27`}
                          className="w-full px-3 py-2 text-sm font-mono border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                        {(() => {
                          const count = dataLayoutStr.split(',').filter(s => /^\d+$/.test(s.trim())).length
                          const needed = compileInfo.n_data || 0
                          return dataLayoutStr.trim() && count !== needed ? (
                            <p className="text-[11px] text-red-500 mt-1">You entered {count} qubits, need exactly {needed}</p>
                          ) : null
                        })()}
                      </div>
                      {/* Ancilla qubits */}
                      <div>
                        <label className="text-xs font-semibold text-orange-700 flex items-center gap-1.5 mb-1">
                          <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />
                          Ancilla Qubits — need {compileInfo.n_ancilla} physical qubits
                        </label>
                        <input type="text" value={ancLayoutStr} onChange={e => setAncLayoutStr(e.target.value)}
                          placeholder={`Enter ${compileInfo.n_ancilla} physical qubit IDs, e.g. 28, 29, 30, 31, 32, 33, 34, 35`}
                          className="w-full px-3 py-2 text-sm font-mono border-2 border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
                        {(() => {
                          const count = ancLayoutStr.split(',').filter(s => /^\d+$/.test(s.trim())).length
                          const needed = compileInfo.n_ancilla || 0
                          return ancLayoutStr.trim() && count !== needed ? (
                            <p className="text-[11px] text-red-500 mt-1">You entered {count} qubits, need exactly {needed}</p>
                          ) : null
                        })()}
                      </div>
                      {/* Summary */}
                      {layoutQubits.length > 0 && (
                        <div className="bg-white border border-slate-200 rounded-md p-2 text-xs space-y-1">
                          <div className="flex items-center gap-1 text-blue-700">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            Data: {dataPhysQubits.map((pq, i) => `D${i}→Q${pq}`).join(', ')}
                          </div>
                          <div className="flex items-center gap-1 text-orange-700">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            Ancilla: {ancillaPhysQubits.map((pq, i) => `A${i}→Q${pq}`).join(', ')}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md">
                      Click <b>"Compile"</b> in the toolbar first to determine qubit requirements.
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-semibold text-qcloud-text">Qubit Topology</h5>
                    <span className="text-xs text-qcloud-muted">Hover qubits for error details</span>
                  </div>
                  <HardwareTopology data={calibData} highlightedQubits={layoutQubits} highlightLabels={layoutLabels} highlightColors={highlightColors} width={800} height={460} />
                  {compileInfo && dataPhysQubits.length > 0 && (
                    <div className="flex gap-4 mt-2 text-xs justify-center">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500" /> Data ({compileInfo.n_data})</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-500" /> Ancilla ({compileInfo.n_ancilla})</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
